import { useRef, useState, type FormEvent } from 'react';
import Modal, { inputCls, labelCls } from '@/components/base/Modal';
import { saveDocument, uploadDocumentFile, deleteDocumentFile, DOCUMENT_TYPE_LABELS, ENTITY_TYPE_LABELS } from '@/services/documents';

interface DocumentModalProps {
  onClose: () => void;
  onSaved: () => void;
}

const docTypeKeys = Object.keys(DOCUMENT_TYPE_LABELS);
const entityTypeKeys = Object.keys(ENTITY_TYPE_LABELS);

export default function DocumentModal({ onClose, onSaved }: DocumentModalProps) {
  const [documentType, setDocumentType] = useState('sale_receipt');
  const [relatedEntityType, setRelatedEntityType] = useState('sale');
  const [fileUrl, setFileUrl] = useState('');
  const [fileName, setFileName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadedRef = useRef<string[]>([]);
  const savedRef = useRef(false);

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError('');

    const { url, error: upErr } = await uploadDocumentFile(file);
    setUploading(false);

    if (upErr) {
      setError(`Error al subir "${file.name}": ${upErr}`);
      return;
    }
    if (url) {
      uploadedRef.current.push(url);
      setFileUrl(url);
      setFileName(file.name);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  // Quita el archivo solo de la vista local. El borrado real en Storage se
  // ejecuta al guardar, para que "cancelar" no deje archivos huérfanos.
  function removeFile() {
    setFileUrl('');
    setFileName('');
  }

  // Cierra el modal. Si no se guardó, limpia los archivos subidos en esta
  // sesión para no dejar huérfanos en Storage.
  async function handleClose() {
    if (!savedRef.current) {
      for (const url of uploadedRef.current) {
        await deleteDocumentFile(url);
      }
    }
    onClose();
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');

    setSaving(true);
    const { error: saveError } = await saveDocument({
      documentType,
      relatedEntityType,
      relatedEntityId: '',
      fileUrl,
    });
    setSaving(false);

    if (saveError) {
      setError(saveError);
      return;
    }

    // Borra de Storage los archivos subidos que quedaron sin usar (quitados
    // con la "X" o reemplazados por una URL pegada). El que quedó como fileUrl
    // final se conserva.
    for (const url of uploadedRef.current) {
      if (url !== fileUrl.trim()) {
        await deleteDocumentFile(url);
      }
    }

    savedRef.current = true;
    onSaved();
    onClose();
  }

  return (
    <Modal title="Nuevo Documento" subtitle="Registra un documento generado con su archivo." onClose={handleClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2.5">
            <i className="ri-error-warning-line"></i>
            <span>{error}</span>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Tipo de documento</label>
            <select className={inputCls} value={documentType} onChange={(e) => setDocumentType(e.target.value)}>
              {docTypeKeys.map((t) => (
                <option key={t} value={t}>{DOCUMENT_TYPE_LABELS[t]}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Entidad relacionada</label>
            <select className={inputCls} value={relatedEntityType} onChange={(e) => setRelatedEntityType(e.target.value)}>
              {entityTypeKeys.map((t) => (
                <option key={t} value={t}>{ENTITY_TYPE_LABELS[t]}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className={labelCls}>Archivo</label>

          {fileName ? (
            <div className="flex items-center gap-2 rounded-md border border-background-200 bg-background-50 px-3 py-2.5">
              <i className="ri-file-text-line text-accent-600 text-lg"></i>
              <span className="flex-1 text-sm text-foreground-800 truncate">{fileName}</span>
              <button
                type="button"
                onClick={removeFile}
                className="w-7 h-7 flex items-center justify-center rounded-full text-foreground-500 hover:bg-background-200 hover:text-red-600 transition-colors cursor-pointer"
                aria-label="Quitar archivo"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
          ) : (
            <>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={handleFile}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="inline-flex items-center gap-2 text-sm px-4 py-2.5 border border-dashed border-background-300 text-foreground-700 rounded-lg hover:bg-background-50 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {uploading ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>Subiendo...
                  </>
                ) : (
                  <>
                    <i className="ri-upload-cloud-2-line"></i>Subir archivo
                  </>
                )}
              </button>
              <p className="text-xs text-foreground-400 mt-1.5">
                Opcional. Se sube a Storage y se guarda la URL pública. También puedes pegar una URL abajo.
              </p>
            </>
          )}
        </div>

        <div>
          <label className={labelCls}>URL del archivo</label>
          <input className={inputCls} value={fileUrl} onChange={(e) => { setFileUrl(e.target.value); setFileName(''); }} placeholder="https://storage.../documento.pdf" />
        </div>

        <div className="flex gap-3 pt-1">
          <button type="button" onClick={handleClose} className="flex-1 px-4 py-2.5 text-sm border border-background-300 text-foreground-700 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer">
            Cancelar
          </button>
          <button type="submit" disabled={saving} className="flex-1 px-4 py-2.5 text-sm font-semibold bg-accent-500 text-white rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed">
            {saving ? (<><i className="ri-loader-4-line animate-spin mr-1.5"></i>Guardando...</>) : (<><i className="ri-save-line mr-1.5"></i>Crear documento</>)}
          </button>
        </div>
      </form>
    </Modal>
  );
}